import type {
  NoticeDto,
  PaginatedResponseDto,
  PaymentRecordDto,
  WithdrawalRecordDto,
  WithdrawalRequestDto,
} from '../../domain/contracts/api.contracts';
import { ClaimStatus, PaymentChannel, PaymentStatus } from '../../domain/enums/SystemEnums';
import { paisaToTaka, paisaToWire, takaToPaisa, toPaisa } from '../../domain/money';

/**
 * Wire ↔ domain adapters for the real backend (sristi-green-backend API_CONTRACT.md).
 *
 * The UI and mocks still speak the webapp domain shapes (numeric money, VERIFIED, BANK_DEPOSIT, …).
 * These helpers translate at the HTTP boundary only and are tolerant: values already in domain form
 * (the MSW mocks) pass through unchanged, so mock mode keeps working.
 */

// --- Money (API_CONTRACT §2.8 / BD-13: decimal strings with two places) ---

const MONEY_KEYS = new Set([
  'amount',
  'commissionAmount',
  'bookingAmount',
  'monthlyAmount',
  'advanceBalance',
  'prepaidAmount',
  'totalEarned',
  'availableBalance',
  'pendingWithdrawals',
  'totalWithdrawn',
  'availableCommissionBalance',
  'totalCommissionEarned',
  'availableCommission',
  'availableForWithdrawal',
  'totalServiceCharge',
  'grossAmount',
  'feeAmount',
  'netAmount',
  'activeVolumeBDT',
  'monthlyAmountBDT',
  'advanceBalanceBDT',
  'nextDueAmountBDT',
  'arrearsBDT',
  'amountBDT',
  'totalPaid',
]);

/** Convert one money value (`"12500.00"` or a number) to the number the UI works with. */
export function moneyToNumber(value: unknown): number {
  return paisaToTaka(toPaisa(value));
}

/** Deep-copy `value`, turning decimal-string money fields into numbers. */
export function normalizeMoney<T>(value: T): T {
  if (Array.isArray(value)) return value.map((v) => normalizeMoney(v)) as unknown as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = MONEY_KEYS.has(k) && typeof v === 'string' ? moneyToNumber(v) : normalizeMoney(v);
    }
    return out as T;
  }
  return value;
}

/** Domain number → wire decimal string with two places. */
export function moneyToWire(amount: number): string {
  return paisaToWire(takaToPaisa(amount));
}

// --- Enums ---

const PAYMENT_CHANNEL_FROM_WIRE: Record<string, PaymentChannel> = {
  BANK_SLIP: PaymentChannel.BANK_DEPOSIT,
};
const PAYMENT_CHANNEL_TO_WIRE: Record<string, string> = {
  [PaymentChannel.BANK_DEPOSIT]: 'BANK_SLIP',
};

// EXPIRED / CANCELLED are terminal non-credit outcomes; the member UI shows them as rejected.
const PAYMENT_STATUS_FROM_WIRE: Record<string, PaymentStatus> = {
  APPROVED: PaymentStatus.VERIFIED,
  EXPIRED: PaymentStatus.REJECTED,
  CANCELLED: PaymentStatus.REJECTED,
};
const PAYMENT_STATUS_TO_WIRE: Record<string, string> = {
  [PaymentStatus.VERIFIED]: 'APPROVED',
};

const WITHDRAWAL_STATUS_FROM_WIRE: Record<string, ClaimStatus> = {
  OTP_PENDING: ClaimStatus.PENDING_VERIFICATION,
};
const WITHDRAWAL_STATUS_TO_WIRE: Record<string, string> = {
  [ClaimStatus.PENDING_VERIFICATION]: 'OTP_PENDING',
};

export function paymentChannelToWire(channel: string): string {
  return PAYMENT_CHANNEL_TO_WIRE[channel] ?? channel;
}
export function paymentStatusToWire(status: string): string {
  return PAYMENT_STATUS_TO_WIRE[status] ?? status;
}
export function withdrawalStatusToWire(status: string): string {
  return WITHDRAWAL_STATUS_TO_WIRE[status] ?? status;
}

// --- Payments (E13, E18, E21) ---

/** MemberPaymentRecordDto → PaymentRecordDto. */
export function paymentFromWire(w: Record<string, unknown>): PaymentRecordDto {
  const channel = String(w.channel);
  const status = String(w.status);
  return {
    id: String(w.id),
    subscriptionId: String(w.subscriptionId),
    channel: (PAYMENT_CHANNEL_FROM_WIRE[channel] ?? channel) as PaymentChannel,
    status: (PAYMENT_STATUS_FROM_WIRE[status] ?? status) as PaymentStatus,
    amount: moneyToNumber(w.amount),
    trxId: typeof w.trxId === 'string' ? w.trxId : undefined,
    ...(typeof w.senderNumber === 'string' ? { senderNumber: w.senderNumber } : {}),
    createdAt: String(w.createdAt),
  };
}

export function paymentPageFromWire(
  w: PaginatedResponseDto<Record<string, unknown>>,
): PaginatedResponseDto<PaymentRecordDto> {
  return { ...w, items: (w.items ?? []).map(paymentFromWire) };
}

// --- Team Navigator / Member 360 (E22–E25) ---

const channelFromWire = (c: unknown) =>
  typeof c === 'string' ? ((PAYMENT_CHANNEL_FROM_WIRE[c] ?? c) as PaymentChannel) : c;

/**
 * E22–E25 wire → domain. Money strings become numbers, backend payment enums (BANK_SLIP, APPROVED) become the
 * webapp's, and every card/breadcrumb gets `apartmentId` (mirrors `userId` when an older server omits it).
 */
export function teamFromWire<T>(value: T): T {
  const walk = (v: unknown, key?: string): unknown => {
    if (Array.isArray(v)) return v.map((x) => walk(x, key));
    if (!v || typeof v !== 'object') return v;
    const src = v as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(src)) {
      if (MONEY_KEYS.has(k) && typeof x === 'string') out[k] = moneyToNumber(x);
      else if (k === 'channel') out[k] = channelFromWire(x);
      else if (k === 'status' && key === 'recentPayments' && typeof x === 'string') {
        out[k] = (PAYMENT_STATUS_FROM_WIRE[x] ?? x) as PaymentStatus;
      } else out[k] = walk(x, k);
    }
    if (typeof out.userId === 'string' && typeof out.apartmentId !== 'string') out.apartmentId = out.userId;
    return out;
  };
  return walk(value) as T;
}

// --- Withdrawals (E14, E19, payout-options) ---

/** WithdrawalRequestDto → E14 wire body: amount + method only (the payout account is the member's saved detail). */
export function withdrawalRequestToWire(body: WithdrawalRequestDto): Record<string, unknown> {
  return { amount: moneyToWire(body.amount), method: body.method };
}

/** Backend WithdrawalRecordDto → webapp WithdrawalRecordDto. */
export function withdrawalFromWire(w: Record<string, unknown>): WithdrawalRecordDto {
  const status = String(w.status);
  return {
    id: String(w.id),
    grossAmount: moneyToNumber(w.grossAmount),
    feeAmount: moneyToNumber(w.feeAmount),
    netAmount: moneyToNumber(w.netAmount),
    payoutMethod: (w.method ?? w.payoutMethod) as WithdrawalRecordDto['payoutMethod'],
    status: (WITHDRAWAL_STATUS_FROM_WIRE[status] ?? status) as ClaimStatus,
    transactionId: typeof w.transactionId === 'string' && w.transactionId !== '' ? w.transactionId : null,
    requestedAt: String(w.requestedAt),
    // Not on the backend record (voucherNumber is the disbursal marker); the UI treats null as "not yet".
    disbursedAt: typeof w.disbursedAt === 'string' ? w.disbursedAt : null,
  };
}

export function withdrawalPageFromWire(
  w: PaginatedResponseDto<Record<string, unknown>>,
): PaginatedResponseDto<WithdrawalRecordDto> {
  return { ...w, items: (w.items ?? []).map(withdrawalFromWire) };
}

// --- Notices (E16) ---

/** Backend NoticeDto carries `publishAt`; the webapp reads `publishedAt`. */
export function noticeFromWire(w: Record<string, unknown>): NoticeDto {
  return {
    id: String(w.id),
    category: w.category as NoticeDto['category'],
    title: String(w.title),
    body: String(w.body),
    targetTiers: (w.targetTiers ?? []) as NoticeDto['targetTiers'],
    publishedAt: String(w.publishedAt ?? w.publishAt),
    expiresAt: typeof w.expiresAt === 'string' ? w.expiresAt : null,
  };
}

